/**
 * TouchControls — Mobile touch input overlay.
 *
 * Adds virtual joystick (left) and action buttons (right) for mobile play.
 * Auto-detects touch capability and shows controls only on touch devices.
 *
 * Left side: virtual joystick for movement (WASD equivalent)
 * Right side: buttons for Jump, Break, Place, Inventory
 */

export class TouchControls {
  constructor(inputManager) {
    this.input = inputManager;
    this.active = false;
    this._joystickX = 0; // -1 to 1
    this._joystickY = 0; // -1 to 1
    this._touching = false;

    // Only show on touch devices
    if (!('ontouchstart' in window)) return;
    this.active = true;
    this._build();
  }

  _build() {
    // Joystick container (left side)
    this._joystickArea = document.createElement('div');
    this._joystickArea.style.cssText = `
      position:fixed; bottom:20px; left:20px; width:120px; height:120px;
      border-radius:50%; background:rgba(255,255,255,0.15);
      border:2px solid rgba(255,255,255,0.3); z-index:300;
      touch-action:none; pointer-events:auto;
    `;

    this._joystickKnob = document.createElement('div');
    this._joystickKnob.style.cssText = `
      width:50px; height:50px; border-radius:50%;
      background:rgba(255,255,255,0.4); position:absolute;
      top:50%; left:50%; transform:translate(-50%,-50%);
      pointer-events:none;
    `;
    this._joystickArea.appendChild(this._joystickKnob);

    // Touch handling for joystick
    this._joystickArea.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this._touching = true;
      this._updateJoystick(e.touches[0]);
    });
    this._joystickArea.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (this._touching) this._updateJoystick(e.touches[0]);
    });
    this._joystickArea.addEventListener('touchend', () => {
      this._touching = false;
      this._joystickX = 0;
      this._joystickY = 0;
      this._joystickKnob.style.transform = 'translate(-50%,-50%)';
    });

    // Action buttons (right side)
    this._buttonsArea = document.createElement('div');
    this._buttonsArea.style.cssText = `
      position:fixed; bottom:20px; right:20px; z-index:300;
      display:flex; flex-direction:column; gap:10px; align-items:flex-end;
    `;

    const btnStyle = `
      width:60px; height:60px; border-radius:12px;
      background:rgba(255,255,255,0.2); border:2px solid rgba(255,255,255,0.4);
      display:flex; align-items:center; justify-content:center;
      font-size:24px; color:white; touch-action:none; pointer-events:auto;
      user-select:none; -webkit-user-select:none;
    `;

    // Jump button
    const jumpBtn = document.createElement('div');
    jumpBtn.innerHTML = '⬆';
    jumpBtn.style.cssText = btnStyle;
    jumpBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.input._simulateKey('Space', true);
    });
    jumpBtn.addEventListener('touchend', () => {
      this.input._simulateKey('Space', false);
    });

    // Break button
    const breakBtn = document.createElement('div');
    breakBtn.innerHTML = '⛏';
    breakBtn.style.cssText = btnStyle;
    breakBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.input._simulateClick(0);
    });

    // Place button
    const placeBtn = document.createElement('div');
    placeBtn.innerHTML = '🧱';
    placeBtn.style.cssText = btnStyle;
    placeBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.input._simulateClick(2);
    });

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;';
    row.appendChild(breakBtn);
    row.appendChild(placeBtn);

    this._buttonsArea.appendChild(jumpBtn);
    this._buttonsArea.appendChild(row);

    document.body.appendChild(this._joystickArea);
    document.body.appendChild(this._buttonsArea);
  }

  _updateJoystick(touch) {
    const rect = this._joystickArea.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = touch.clientX - cx;
    const dy = touch.clientY - cy;
    const maxDist = rect.width / 2 - 25;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxDist);
    const angle = Math.atan2(dy, dx);

    this._joystickX = (dist / maxDist) * Math.cos(angle);
    this._joystickY = (dist / maxDist) * Math.sin(angle);

    const knobX = this._joystickX * maxDist;
    const knobY = this._joystickY * maxDist;
    this._joystickKnob.style.transform = `translate(calc(-50% + ${knobX}px), calc(-50% + ${knobY}px))`;

    // Simulate WASD keys based on joystick position
    this.input._simulateKey('KeyW', this._joystickY < -0.3);
    this.input._simulateKey('KeyS', this._joystickY > 0.3);
    this.input._simulateKey('KeyA', this._joystickX < -0.3);
    this.input._simulateKey('KeyD', this._joystickX > 0.3);
  }

  getJoystick() {
    return { x: this._joystickX, y: this._joystickY };
  }
}
